import { useEffect, useMemo, useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { StoneXButton, StoneXDatePicker, StoneXLabeledItem, StoneXModal } from '../../StoneXMui';
import { BudgetRoundModel, UpdateBudgetRoundRequest } from '../../../Generated/Commodity-Management-Api';
import { StoneXRow } from '../../StoneX';
import { useApplicationDispatch } from '../../../Redux/ReduxStore';
import { addCustomerBudgetRound } from '../../../Redux/Slices/CommodityManagementSlice';
import { CommodityManagementApi } from '../../../Apis/Apis';
import { useLoadingState } from '../../../Hooks';

type SelectorOption =
  | { type: 'existing'; value: BudgetRoundModel }
  | { type: 'create-text'; inputValue: string }
  | { type: 'create-date' };

interface BudgetRoundCreatePayload {
  source: 'text' | 'date';
  name: string;
  date?: Date | null;
}

interface BudgetRoundSelectorProps {
  options: BudgetRoundModel[];
  value?: BudgetRoundModel | null;
  onChange?: (value: BudgetRoundModel | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

const filterOptions = createFilterOptions<SelectorOption>();

function isExistingOption(option: SelectorOption): option is { type: 'existing'; value: BudgetRoundModel } {
  return option.type === 'existing';
}

function isCreateTextOption(option: SelectorOption): option is { type: 'create-text'; inputValue: string } {
  return option.type === 'create-text';
}

export function BudgetRoundSelector(props: BudgetRoundSelectorProps) {
  const {
    options,
    value = null,
    onChange,
    label = 'Budget Round',
    placeholder = 'Select or type a budget round',
    disabled,
  } = props;

  const dispatch = useApplicationDispatch();

  const loadingState = useLoadingState();

  const [dateToCreateFrom, setDateToCreateFrom] = useState<Date | null>(new Date());
  const [showDateCreator, setShowDateCreator] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedBudgetRound, setSelectedBudgetRound] = useState<BudgetRoundModel | null>(value);

  useEffect(() => {
    setSelectedBudgetRound(value ?? null);
  }, [value]);

  const autocompleteOptions = useMemo(() => {
    return options.map(x => ({ type: 'existing', value: x } as SelectorOption));
  }, [options]);

  const activeBudgetRound = value ?? selectedBudgetRound;
  const selectedOption = activeBudgetRound ? ({ type: 'existing', value: activeBudgetRound } as SelectorOption) : null;

  function onCreate(payload: BudgetRoundCreatePayload) {
    const request = {
      budgetRoundName: payload.name,
    } as UpdateBudgetRoundRequest;
    
    CommodityManagementApi.updateBudgetRound(loadingState.setLoadingState, request)
      .then(res => {
        const createdRound = res.data.budgetRound!;
        dispatch(addCustomerBudgetRound({ budgetRound: createdRound }));
        setSelectedBudgetRound(createdRound);
        setInputValue(createdRound.budgetRoundName);
        onChange?.(createdRound);
      });
  }

  return (
    <StoneXLabeledItem label={label}>
      <Autocomplete
        freeSolo
        disabled={disabled || loadingState.isLoading()}
        loading={loadingState.isLoading()}
        options={autocompleteOptions}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        isOptionEqualToValue={(option, selected) => {
          if (!isExistingOption(option) || !isExistingOption(selected)) {
            return false;
          }

          return option.value.budgetRoundId === selected.value.budgetRoundId;
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') {
            return option;
          }

          if (isExistingOption(option)) {
            return option.value.budgetRoundName;
          }

          if (isCreateTextOption(option)) {
            return `Create "${option.inputValue}"`;
          }

          return 'Create from date...';
        }}
        filterOptions={(currentOptions, params) => {
          const filtered = filterOptions(currentOptions, params);
          const input = params.inputValue.trim();

          if (input.length > 0) {
            const alreadyExists = options.some(x => x.budgetRoundName.toLowerCase() === input.toLowerCase());
            if (!alreadyExists) {
              filtered.push({ type: 'create-text', inputValue: input });
            }
          }

          filtered.push({ type: 'create-date' });
          return filtered;
        }}
        onChange={(_, newValue) => {
          if (newValue == null) {
            setSelectedBudgetRound(null);
            onChange?.(null);
            setInputValue('');
            return;
          }

          if (typeof newValue === 'string') {
            const input = newValue.trim();
            if (input.length > 0) {
              onCreate({ source: 'text', name: input });
            }

            setInputValue('');
            return;
          }

          if (isCreateTextOption(newValue)) {
            onCreate({ source: 'text', name: newValue.inputValue });
            setInputValue('');
            return;
          }

          if (newValue.type === 'create-date') {
            setDateToCreateFrom(new Date());
            setShowDateCreator(true);
            setInputValue('');
            return;
          }

          setInputValue(newValue.value.budgetRoundName);
          setSelectedBudgetRound(newValue.value);
          onChange?.(newValue.value);
        }}
        renderOption={(renderProps, option) => {
          return (
            <li {...renderProps}>
              {isExistingOption(option) && option.value.budgetRoundName}
              {isCreateTextOption(option) && `Create "${option.inputValue}"`}
              {option.type === 'create-date' && 'Create from date...'}
            </li>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingState.isLoading() ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <StoneXModal header={'Create New Budget Round'} open={showDateCreator} onClose={() => setShowDateCreator(false)} >
        <StoneXRow>
          <StoneXDatePicker value={dateToCreateFrom} onChange={setDateToCreateFrom} disabled={loadingState.isLoading()} />
        </StoneXRow>
        <StoneXRow justify='end' marginDirection='top'>
          <StoneXButton variant='outlined' disabled={loadingState.isLoading()} onClick={() => setShowDateCreator(false)}>
            Cancel
          </StoneXButton>
          <StoneXButton
            variant="contained" 
            disabled={!dateToCreateFrom || loadingState.isLoading()}
            isWorking={loadingState.isLoading()}
            onClick={() => {
              if (!dateToCreateFrom) {
                return;
              }

              const dateText = dateToCreateFrom.toLocaleDateString();

              onCreate({
                source: 'date',
                name: dateText,
                date: dateToCreateFrom,
              });

              setInputValue('');
              setShowDateCreator(false);
            }}
          >
            Create
          </StoneXButton>
        </StoneXRow>
        
      </StoneXModal>

    </StoneXLabeledItem>
  );
}
